import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BASE_WIDTH = 1920;
const BASE_HEIGHT = 1080;

const Wrapper = styled.div`
  background-color: ${p => p.theme.backgroundColor};
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.section`
  width: 540px;
  text-align: center;
  padding: 50px 0 120px;
  overflow: hidden;
  position: relative;
  border-radius: 5px;
  display: flex;
  align-items: center;
`;

const SimulatorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const SimulatorScaleWrapper = styled.div<{
  scale: number;
  width: number;
  height: number;
}>`
  position: relative;
  width: ${({ width, scale }) => width * scale}px;
  height: ${({ height, scale }) => height * scale}px;

  & > * {
    position: absolute;
    top: 50%;
    left: 50%;

    width: ${({ width }) => width}px;
    height: ${({ height }) => height}px;
    transform: translate(-50%, -50%) scale(${({ scale }) => scale});
    transform-origin: center center;

    transition: transform 200ms ease;
    will-change: transform;
  }
`;

const Simulator = styled.iframe`
  border: 10px solid #333;
  border-radius: 3px;
  transition: transform 150ms ease;
  will-change: transform;

  &:fullscreen {
    width: 100vw !important;
    height: 100vh !important;
    border: none;
    border-radius: 0;
    transform: none !important;
  }
`;

const InputField = styled.input.attrs({ type: 'number' })`
  border: none;
  border-radius: 3px;
  padding: 10px 15px;
  font-size: 1.5em;
  outline: none;
  max-width: 220px;
  margin: 5px;
`;

const SubmitButton = styled.button`
  background-color: ${p => p.theme.accentColor};
  color: #fff;
  border: none;
  border-radius: 3px;
  padding: 10px 15px;
  font-size: 1.5em;
  outline: none;
  margin: 5px;
  cursor: pointer;
`;

const Form = styled.form`
  margin: 0 auto;
  text-align: center;
`;

const Text = styled.p`
  color: #fff;
  margin: 0 0 10px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 20px;
`;

const Button = styled.button`
  padding: 8px 20px 8px 16px;
  min-height: 42px;
  border-radius: 110px;
  background-color: ${p => p.theme.accentColor};
  color: #fff;
  font-size: 0.9em;
  font-weight: 600;
  text-align: center;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  & svg {
    fill: #fff;
    margin-right: 10px;
  }

  &:active {
    opacity: .9;
  }
`;

/**
 * Render a simulator
 */
const SimulatorView: FC = () => {
  const navigate = useNavigate();
  const { deviceId } = useParams<{ deviceId: string }>();
  const simulatorRef = useRef<HTMLIFrameElement>(null);
  const deviceInputRef = useRef<HTMLInputElement>(null);

  const [simulatorScale, setSimulatorScale] = useState<number>(1);
  const [deviceUrl, setDeviceUrl] = useState<string>('/');
  const [isPortrait, setIsPortrait] = useState(false);

  // Deriverte dimensjoner basert på orientering
  const currentWidth = isPortrait ? BASE_HEIGHT : BASE_WIDTH;   // 1080 eller 1920
  const currentHeight = isPortrait ? BASE_WIDTH : BASE_HEIGHT;  // 1920 eller 1080

  // Is the current browser Internet Explorer?
  const isIE = useCallback(() => {
    const ua = window.navigator.userAgent;
    const oldIE = ua.indexOf('MSIE ');
    const newIE = ua.indexOf('Trident/');
    return oldIE > -1 || newIE > -1;
  }, []);

  // Resize iframe based on available width and height
  const scaleIframe = useCallback((width: number, height: number) => {
    const bodyWidth = document.body.clientWidth;
    const bodyHeight = document.body.clientHeight;

    const maxWidth = bodyWidth - 500; // Padding on sides
    const maxHeight = Math.max(bodyHeight - 200, 200); // Reserve space for buttons

    const widthScale = maxWidth / width;
    const heightScale = maxHeight / height;

    const scale = Math.min(widthScale, heightScale, 1); // Do not scale above 1
    setSimulatorScale(scale);
  }, []);

  // Scale on mount, on resize and on orientation change
  useEffect(() => {
    const handleResize = () => {
      scaleIframe(currentWidth, currentHeight);
    };

    handleResize(); // initial
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [scaleIframe, currentWidth, currentHeight]);

  // Get the device URL
  useEffect(() => {
    if (!deviceId) return;
    setDeviceUrl(`/#/device/${deviceId}`);
  }, [deviceId]);

  /**
   * Navigate to simulator on deviceId input
   */
  const updateDeviceId = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (deviceInputRef.current) {
        navigate(`/simulator/${deviceInputRef.current.value}`);
      }
    },
    [navigate],
  );

  // Rotate orientation (landskap <-> portrett)
  const rotateSimulator = useCallback(() => {
    setIsPortrait(prev => !prev);
  }, []);

  // Reset simulator
  const resetSimulator = useCallback(() => {
    navigate('/simulator/');
  }, [navigate]);

  // Open iframe in fullscreen
  const openFullscreen = useCallback(async () => {
    const iframe = simulatorRef.current;

    if (!iframe) return;

    try {
      if (iframe.requestFullscreen) {
        await iframe.requestFullscreen();
      }
    } catch (error) {
      console.error('Kunne ikke åpne simulator i fullskjerm:', error);
    }
  }, []);

  if (isIE()) {
    return (
      <Wrapper>
        <Text>Internet Explorer er ikke støttet. Vennligst benytt Google Chrome, Safari eller andre nyere nettlesere.</Text>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {deviceId ? (
        <SimulatorWrapper>
          <SimulatorScaleWrapper scale={simulatorScale} width={currentWidth} height={currentHeight}>
            <Simulator src={deviceUrl} ref={simulatorRef} allowFullScreen/>
          </SimulatorScaleWrapper>
          <Buttons>
            <Button type="button" onClick={rotateSimulator}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368"><path d="M522-80v-82q34-5 66.5-18t61.5-34l56 58q-42 32-88 51.5T522-80Zm-80 0Q304-98 213-199.5T122-438q0-75 28.5-140.5t77-114q48.5-48.5 114-77T482-798h6l-62-62 56-58 160 160-160 160-56-56 64-64h-8q-117 0-198.5 81.5T202-438q0 104 68 182.5T442-162v82Zm322-134-58-56q21-29 34-61.5t18-66.5h82q-5 50-24.5 96T764-214Zm76-264h-82q-5-34-18-66.5T706-606l58-56q32 39 51 86t25 98Z"/></svg>
              Roter skjerm
            </Button>
            <Button type="button" onClick={resetSimulator}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"/></svg>
              Bytt enhet
            </Button>
            <Button type="button" onClick={openFullscreen}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"><path d="M120-120v-240h80v104l124-124 56 56-124 124h104v80H120Zm480 0v-80h104L580-324l56-56 124 124v-104h80v240H600ZM324-580 200-704v104h-80v-240h240v80H256l124 124-56 56Zm312 0-56-56 124-124H600v-80h240v240h-80v-104L636-580Z" /></svg>
              Vis fullskjerm
            </Button>
          </Buttons>
        </SimulatorWrapper>
      ) : (
        <Container>
          <Form method="GET" onSubmit={updateDeviceId}>
            <Text>Skriv inn IDen til enheten du ønsker å forhåndsvise</Text>
            <InputField placeholder="ID" ref={deviceInputRef} />
            <SubmitButton type="submit">Vis</SubmitButton>
          </Form>
        </Container>
      )}
    </Wrapper>
  );
};

export default SimulatorView;
